<script setup>
import { Head } from '@inertiajs/vue3'
import { useI18n } from 'vue-i18n'
import PublicLayout from '@/Layouts/PublicLayout.vue'
import BrandHero from '@/Components/Public/BrandHero.vue'
import LuxButton from '@/Components/Public/LuxButton.vue'

// The occasions hub: every kind of day at sea we plan, each a page of its own.
defineOptions({ layout: PublicLayout })

const { t } = useI18n()
defineProps({
  hub:       { type: Object, required: true },  // title, lede
  occasions: { type: Array,  default: () => [] }, // [{slug, title, lede}]
  base_path: { type: String, default: '/occasions' },
  plan_path: { type: String, default: '/plan' }
})
</script>

<template>
  <Head>
    <title>{{ hub.title }} · {{ t('public.charter_home.head_title') }}</title>
    <meta name="description" :content="hub.lede">
  </Head>

  <BrandHero kind="charter" :eyebrow="t('public.occasions.eyebrow')" :title="hub.title">
    <p class="bhero-lede">{{ hub.lede }}</p>
  </BrandHero>

  <section class="body">
    <div class="inner">
      <ul class="cards">
        <li v-for="o in occasions" :key="o.slug">
          <a :href="`${base_path}/${o.slug}`" class="card">
            <h2 class="card__title">{{ o.title }}</h2>
            <p class="card__lede">{{ o.lede }}</p>
            <span class="card__arrow" aria-hidden="true">→</span>
          </a>
        </li>
      </ul>
      <div class="plan-band">
        <p>{{ t('public.occasions.plan_band') }}</p>
        <LuxButton :href="plan_path" variant="solid" tone="light" glyph="arrow">{{ t('public.occasions.plan_cta') }}</LuxButton>
      </div>
    </div>
  </section>
</template>

<style scoped>
.bhero-lede { margin: 1rem 0 0; font-size: clamp(1.05rem, 2vw, 1.3rem); line-height: 1.5; max-width: 52ch; opacity: 0.92; }
.body { background: var(--bg); }
.inner { max-width: 80rem; margin: 0 auto; padding: clamp(2rem, 4vw, 3rem) clamp(1.25rem, 4vw, 3rem) 4rem; display: grid; gap: 2.5rem; }
.cards { list-style: none; margin: 0; padding: 0; display: grid; grid-template-columns: repeat(auto-fill, minmax(16rem, 1fr)); gap: 1.25rem; }
.card { display: grid; gap: 0.6rem; align-content: start; height: 100%; box-sizing: border-box; padding: 1.6rem 1.5rem 1.7rem; background: var(--surface); border: 1px solid var(--line); border-radius: 1.25rem; text-decoration: none; color: inherit; transition: border-color 200ms ease, transform 150ms ease; }
.card:hover { border-color: var(--accent); transform: translateY(-2px); }
.card__title { font-family: var(--font-display, var(--display)); font-weight: 500; font-size: 1.9rem; line-height: 1.05; margin: 0; color: var(--ink); }
.card__lede { margin: 0; color: var(--inkSoft); line-height: 1.5; }
.card__arrow { font-family: var(--font-display, var(--display)); font-size: 1.4rem; color: var(--accent); margin-top: 0.4rem; }
.plan-band { padding: 1.5rem 1.75rem; border-radius: 1.25rem; background: linear-gradient(160deg, var(--sand, var(--surfaceAlt)) 0%, var(--accentSoft) 100%); display: flex; flex-wrap: wrap; gap: 1rem; align-items: center; justify-content: space-between; }
.plan-band p { margin: 0; color: var(--ink); font-family: var(--font-display, var(--display)); font-size: 1.35rem; line-height: 1.2; max-width: 40ch; }
</style>
